import React, { useState, useEffect } from "react";
import get from "lodash/get";
import throttle from "lodash/throttle";
import forEach from "lodash/forEach";
import invoke from "lodash/invoke";

import videojs from "video.js";
import qualitySelector from "videojs-hls-quality-selector";
import qualityLevels from "videojs-contrib-quality-levels";
import "videojs-seek-buttons";
import "videojs-overlay";
import "videojs-hotkeys";

import "video.js/dist/video-js.css";
import "./vjs-theme-dt.css";

import "./videojs-seek-buttons.css";

import "video.js/dist/lang/zh-TW.json";

import { useHistory } from "react-router-dom";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import {
  useUserState,
  signOut,
  useUserDispatch,
} from "../../context/UserContext";
import VideoQuotaExceedDialog from "../../components/VideoQuotaExceedDialog";

import {
  getUnixTs,
  getMovieTime,
  saveMovieTime,
  deleteMovieTime,
  getToken,
  getTabID,
  getPreferQuality,
  setPreferQuality,
  elementReady,
  isEmpty,
} from "../../utils/common";
import { videoHeartBeatSec, videoSaveTimeIntervalSec } from "../../utils/const";
import tracker from "../../utils/tracker";
import {
  createUserWatchHistory,
  sendQuotaHeartBeat,
  reserveQuota,
  releaseQuota,
} from "../../clientAPI";

import langZhTw from "./langZhTW";

export default function VideoJsPlayer ({
  forwardedRef,
  options,
  videoInfoState,
}) {
  const history = useHistory();
  const { trackEvent, pushInstruction } = useMatomo();
  const userDispatch = useUserDispatch();

  const { isAuthenticated } = useUserState();

  const [player, setPlayer] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);

  const video = get(videoInfoState, "video");
  const videoId = video && video.id;
  const tabId = getTabID();
  const videoType =
    window.location.pathname.indexOf("trailer") !== -1 ? "trailer" : "movie";
  const isMovie = video && video.id && videoType === "movie";
  const needQuota = isMovie || isAuthenticated;
  const videoTitle = `${get(video, "title_zh")}（${
    isMovie ? "正片" : "預告片"
  }）`;
  const videoUrl = get(options, "sources.0.src");

  const handleReserveQuotaErr = err => {
    if ([400, 401].includes(get(err, "response.status"))) {
      signOut(userDispatch, history, pushInstruction);
    }
  };

  const handlePlaybackStarted = async e => {
    if (!video.id || !videoType) {
      return;
    }
    createUserWatchHistory(video.id, videoType).catch(err => {
      console.log(err);
    });
  };

  const handleMovieContinuePlay = player => {
    if (player && isMovie && getToken()) {
      const lastMovieTime = getMovieTime(video.id);
      const duration = Number.parseInt(player ? player.duration() : -1);
      if (lastMovieTime !== duration) {
        player.currentTime(lastMovieTime);
      }
    }
  };

  const getMaxQuality = qualityLevels => {
    if (isEmpty(qualityLevels.levels_)) {
      return;
    }
    return `${qualityLevels[0].height}p`;
  };

  const handleClickQualityV1 = quality => {
    elementReady(".vjs-quality-selector > div > ul > li.vjs-menu-item").then(
      () => {
        setTimeout(() => {
          const nodeList = document.querySelectorAll(
            ".vjs-quality-selector > div > ul > li.vjs-menu-item",
          );
          if (!nodeList) {
            return;
          }
          if (!quality && nodeList.length > 1) {
            nodeList[nodeList.length - 2].handleClick();
          } else if (quality === "auto") {
            nodeList[nodeList.length - 1].handleClick();
          } else {
            forEach(nodeList, node => {
              if (
                node.querySelector(".vjs-menu-item-text").innerText === quality
              ) {
                node.click();
                return;
              }
            });
          }
        }, 500);
      },
    );
  };

  const handleClickQualityV2 = (player, quality) => {
    elementReady(".vjs-quality-selector > div > ul > li.vjs-menu-item").then(
      () => {
        const hlsQualitySelector = player.hlsQualitySelector;
        const nodeList = hlsQualitySelector._qualityButton.items;
        if (!nodeList) {
          return;
        }
        if (!quality && nodeList.length > 1) {
          nodeList[nodeList.length - 2].handleClick();
        } else if (quality === "auto") {
          nodeList[nodeList.length - 1].handleClick();
        } else {
          forEach(nodeList, node => {
            if (`${node.item.value}p` === quality) {
              node.handleClick();
              return;
            }
          });
        }
      },
    );
  };

  const setPlayerQuality = player => {
    const quality = getPreferQuality();
    // set if exist or set max quality
    if (quality) {
      if (quality === "auto") {
        handleClickQualityV2(player, "auto");
        return;
      }
      const height = quality.slice(0, -1);
      const qualityLevels = player.qualityLevels();

      if (isEmpty(qualityLevels.levels_)) {
        setTimeout(() => {
          const qualityLevels = player.qualityLevels();
          // handleClickQualityV1(quality)
          handleClickQualityV2(player, quality);
        }, 500);
      } else {
        handleClickQualityV2(player, quality);
      }
    } else {
      const maxQuailty = getMaxQuality(qualityLevels);
      handleClickQualityV2(player, maxQuailty);
    }
  };

  const listenQualityButtonClick = player => {
    elementReady(".vjs-quality-selector > div > ul > li.vjs-menu-item").then(
      () => {
        const hlsQualitySelector = player.hlsQualitySelector;
        const nodeList = hlsQualitySelector._qualityButton.items;
        forEach(nodeList, node => {
          node.on("click", () => {
            if (node.item.value === "auto") {
              setPreferQuality(node.item.value);
            } else {
              setPreferQuality(`${node.item.value}p`);
            }
          });
        });
      },
    );
  };

  const savePlayerCurrentTime = () => {
    const isPlaying = !invoke(player, "paused");
    if (isMovie && player && isPlaying) {
      const currentTime = Number.parseInt(player.currentTime());
      const lastMovieTime = getMovieTime(video.id);
      if (currentTime !== lastMovieTime) {
        saveMovieTime(video.id, currentTime);
      }
    }
  };

  const handleHeartbeat = () => {
    const isPlaying = !invoke(player, "paused");
    if (!needQuota && player && isPlaying) {
      const currentTime = Number.parseInt(player.currentTime());
      if (currentTime < 1) {
        return;
      }
      const duration = player ? player.duration() : -1;
      tracker.watchedVideo({
        trackEvent,
        pushInstruction,
        videoType,
        videoId,
        tabId,
        spent: videoHeartBeatSec,
        currentTime,
        duration,
      });
      return;
    }
    if (needQuota && player && isPlaying) {
      const currentTime = Number.parseInt(player.currentTime());
      const lastMovieTime = getMovieTime(video.id);
      if (currentTime < 1) {
        return;
      }

      const duration = player ? player.duration() : -1;
      if (currentTime === duration) {
        releaseQuota();
      }

      const ts = getUnixTs();
      sessionStorage.setItem("heartbeat", ts);
      sendQuotaHeartBeat(video.id)
        .then(data => {
          if (data.msg === "error") {
            reserveQuota(video.id)
              .then(async data => {
                if (data.msg === "error" && data.has_quota === false) {
                  player.pause();
                  player.trigger("noquota");
                  setOpenDialog(true);
                  tracker.recordNoQuota({
                    trackEvent,
                    videoId,
                    videoType,
                    quotaCnt: data.quota_cnt,
                  });
                } else {
                  tracker.watchedVideo({
                    trackEvent,
                    pushInstruction,
                    videoType,
                    videoId,
                    tabId,
                    spent: videoHeartBeatSec,
                    currentTime,
                    duration,
                  });
                }
              })
              .catch(err => {
                console.log("reserveQuota wrong:", err);
                handleReserveQuotaErr(err);
              });
          } else {
            tracker.watchedVideo({
              trackEvent,
              pushInstruction,
              videoType,
              videoId,
              tabId,
              spent: videoHeartBeatSec,
              currentTime,
              duration,
            });
          }
        })
        .catch(err => {
          console.log("sendQuotaHeartBeatErr", err);
          handleReserveQuotaErr(err);
        });
    }
  };

  const handleHeartbeatThrottle = throttle(
    handleHeartbeat,
    videoHeartBeatSec * 1000,
  );

  useEffect(() => {
    if (!player) return;
    let id = setInterval(() => {
      handleHeartbeatThrottle();
    }, videoHeartBeatSec * 1000);
    return () => clearInterval(id);
  }, [player]);

  useEffect(() => {
    if (!player) return;
    let id = setInterval(() => {
      savePlayerCurrentTime();
    }, videoSaveTimeIntervalSec * 1000);
    return () => clearInterval(id);
  }, [player]);

  useEffect(() => {
    if (!forwardedRef) return;

    if (player && player.currentSrc() === videoUrl) {
      return;
    }

    videojs.addLanguage("zh-TW", langZhTw);
    const initPlayer = videojs(
      forwardedRef.current,
      options,
      function onPlayerReady () {
        initPlayer.trigger("player-ready");
        handleMovieContinuePlay(initPlayer);
        initPlayer.hlsQualitySelector({ displayCurrentQuality: true });
        setPlayerQuality(initPlayer);
        listenQualityButtonClick(initPlayer);

        initPlayer.on("play", e => {
          handlePlaybackStarted();
          const currentTime = Number.parseInt(initPlayer.currentTime());
          const duration = initPlayer.duration();
          tracker.watchedVideo({
            trackEvent,
            pushInstruction,
            videoType,
            videoId,
            tabId,
            spent: 1,
            currentTime,
            duration,
          });
          if (needQuota) {
            reserveQuota(video.id)
              .then(async data => {
                if (data.msg === "error" && data.has_quota === false) {
                  initPlayer.pause();
                  initPlayer.trigger("noquota");
                  setOpenDialog(true);
                  tracker.recordNoQuota({
                    trackEvent,
                    videoId,
                    videoType,
                    quotaCnt: data.quota_cnt,
                  });
                } else {
                  tracker.playedVideo({
                    trackEvent,
                    pushInstruction,
                    videoType,
                    videoId,
                    tabId,
                  });
                }
              })
              .catch(err => {
                console.log("reserveQuota wrong:", err);
                handleReserveQuotaErr(err);
              });
          } else {
            tracker.playedVideo({
              trackEvent,
              pushInstruction,
              videoType,
              videoId,
              tabId,
            });
          }
        });
        // video is actually playing
        // https://stackoverflow.com/questions/14265332/html5-play-event-vs-playing-event-in-video-tag
        initPlayer.on("playing", e => {});
        initPlayer.on("loadedmetadata", e => {
          const textTracks = initPlayer.textTracks();
          forEach(textTracks, track => {
            if (
              ["subtitles", "captions"].includes(track.kind) &&
              track.language === "zh"
            ) {
              track.mode = "showing";
            }
          });
        });
        initPlayer.on("pause", e => {});
        initPlayer.on("ended", e => {
          deleteMovieTime(video.id);
          releaseQuota();
        });
        initPlayer.on("error", e => {
          console.error("player:", e, initPlayer.error());
        });
        initPlayer.on("noquota", () => {
          if (initPlayer.isFullscreen()) {
            if (document.webkitExitFullscreen) {
              /* Safari */
              document.webkitExitFullscreen();
            } else if (document.webkitCancelFullScreen) {
              document.webkitCancelFullScreen();
            } else if (document.mozCancelFullScreen) {
              document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
              /* IE11 */
              document.msExitFullscreen();
            } else if (document.exitFullscreen) {
              document.exitFullscreen();
            }
          }
        });
      },
    );
    setPlayer(initPlayer);

    return () => {
      if (player) player.dispose();
    };
  }, [videoUrl, options, player]);

  useEffect(() => {
    if (player) {
      player.seekButtons({
        forward: 10,
        back: 10,
      });
      player.overlay({
        overlays: [
          {
            content: videoTitle,
            class: "vjs-overlay-title",
            start: "pause",
            end: "play",
          },
          {
            content: videoTitle,
            class: "vjs-overlay-title",
            start: "player-ready",
            end: "play",
          },
        ],
      });
      player.hotkeys({
        volumeStep: 0.1,
        seekStep: 10,
        enableNumbers: false,
      });
      player.getChild("controlBar").addChild("Spacer", {}, 6);
    }
  }, [player]);

  return (
    <div key={videoUrl}>
      <div data-vjs-player>
        <video ref={forwardedRef} className='video-js vjs-theme-dt' />
      </div>
      <VideoQuotaExceedDialog open={openDialog} setOpen={setOpenDialog} />
    </div>
  );
}
